import { z } from 'zod';
/**
 * §7's operational alert rules.
 *
 * ⚠️ **EVERY RULE DECLARES ITS DATA SOURCE, and that is §7's own requirement
 * rather than a design flourish:** *"Each alert rule type shall declare its data
 * source"*, followed by *"Provider inactivity, queue backlog and long-running
 * jobs … shall **not** be inferred from the absence of logs."*
 *
 * That second sentence is the one with teeth. Inferring a provider outage from
 * "no log lines about this provider" is wrong in the most dangerous direction:
 * a logging pipeline that has stopped looks exactly like every provider being
 * silent, so one fault would raise dozens of alerts about a system that is
 * working. The declaration makes the rule checkable — `platformAlertRules.test`
 * asserts that no rule whose subject is a non-log condition reads the log store.
 *
 * ⚠️ **THRESHOLDS ARE TUNED FOR "READ IN THE MORNING", NOT "PAGED AT 3AM"**, and
 * that follows from a fact rather than a preference: ADR 0011 records that there
 * is no on-call rota. An alert nobody is awake to act on is noise that teaches a
 * team to skim the list, which costs more than the alert was worth.
 */
/**
 * Where a rule's evidence comes from.
 *
 * ⚠️ A CLOSED list, so a rule cannot invent a source — and so the guard that
 * enforces §7's "not inferred from the absence of logs" has something to check
 * against.
 */
export const ALERT_DATA_SOURCES = [
    /** §6's five-minute summary tables. Never the raw log table. */
    'LOG_SUMMARY',
    /** `pg_total_relation_size` over the log store's partitions. */
    'LOG_STORAGE_METRICS',
    /** `LogWriterHeartbeat` — in the TRANSACTIONAL database, by §7's own rule. */
    'WRITER_HEARTBEAT',
    /** `ProviderConnection.polledAt` — the last SUCCESS, never the last attempt. */
    'PROVIDER_STATE',
    /** Queue depth, asked of the queue system itself. */
    'QUEUE',
    /** `ScheduledJobLease` and `JobRun` — when a run started and whether it ended. */
    'SCHEDULER_STATE',
];
/**
 * ⚠️ **THE SOURCES THAT ARE NOT THE LOG STORE**, named so the guard can be
 * written as a positive statement rather than a list of exceptions. A rule about
 * a provider, a queue or a job must read one of these.
 */
export const NON_LOG_SOURCES = [
    'PROVIDER_STATE',
    'QUEUE',
    'SCHEDULER_STATE',
];
export const ALERT_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
export const alertSeveritySchema = z.enum(ALERT_SEVERITIES);
/**
 * Every rule, in §7's own order.
 *
 * ⚠️ `as const satisfies` for the reason `ANOMALY_FEATURES` gives: the literal
 * type makes a missing field a compile error, and `alertRule()` below is the
 * widened reading surface.
 */
export const PLATFORM_ALERT_RULES = {
    /** §7: "error count / error rate". */
    ERROR_RATE: {
        label: 'Errors above the usual rate',
        source: 'LOG_SUMMARY',
        severity: 'WARNING',
        remedy: 'Open the log dashboard for the same window and look at which service and which code is responsible. A single code dominating usually means one fault; a spread across services usually means a dependency.',
        cooldownMinutes: 60,
    },
    /** §7: "repeated error code". */
    REPEATED_ERROR_CODE: {
        label: 'One error code repeating',
        source: 'LOG_SUMMARY',
        severity: 'WARNING',
        remedy: 'Search the logs for this code over the same window. The code is registered, so its owner and its description say which package owns the fix.',
        cooldownMinutes: 120,
    },
    /** §7: "storage thresholds". */
    LOG_STORAGE: {
        label: 'The log store is filling',
        source: 'LOG_STORAGE_METRICS',
        severity: 'WARNING',
        remedy: 'Check that the log store maintenance job is running — expired partitions are dropped daily. If it is healthy, the retention windows are larger than the volume supports and OIP_LOG_RETENTION_DAYS is where that is changed.',
        cooldownMinutes: 12 * 60,
    },
    /**
     * §7 self-monitoring: "when the log writer reports dropped ERROR or security
     * events".
     *
     * ⚠️ CRITICAL, and it is the one rule here that would be. Shedding DEBUG under
     * load is the design working; dropping an ERROR means a line that mattered was
     * never written and can never be recovered — the store cannot tell you about
     * what never reached it.
     */
    WRITER_DROPPED_IMPORTANT: {
        label: 'A log writer dropped errors or security events',
        source: 'WRITER_HEARTBEAT',
        severity: 'CRITICAL',
        remedy: 'The log database is unreachable or too slow to keep up, and lines are being shed. Check the log store is up and that the summary and maintenance jobs are not holding long transactions. ERROR and security lines still reach stdout, so a container log for the same period is the fallback record.',
        cooldownMinutes: 60,
        selfMonitoring: true,
    },
    /** §7 self-monitoring: "any service's writer heartbeat stops for more than 5 minutes". */
    WRITER_SILENT: {
        label: 'A log writer has stopped reporting',
        source: 'WRITER_HEARTBEAT',
        severity: 'WARNING',
        remedy: 'Either the service stopped or its writer did. Check the service is running; a container that was replaced leaves its old instance behind for a few days, which is expected and clears itself.',
        cooldownMinutes: 60,
        selfMonitoring: true,
    },
    /** §7 self-monitoring: "when ingestion lag exceeds its target". */
    INGESTION_LAG: {
        label: 'Log lines are queuing faster than they are written',
        source: 'WRITER_HEARTBEAT',
        severity: 'WARNING',
        remedy: 'The writer is behind, so a search will not show the most recent minutes. If the queue keeps growing it will begin shedding — debug first, then info, then warn. Check the log database for load.',
        cooldownMinutes: 60,
        selfMonitoring: true,
    },
    /**
     * §7: "provider inactivity".
     *
     * ⚠️ **READ FROM PROVIDER STATE, NEVER FROM THE ABSENCE OF LOGS**, which §7
     * says in as many words. A logging pipeline that has stopped looks exactly
     * like every provider going quiet, so inferring it would turn one fault into
     * an alert per customer — about a system that is working.
     */
    PROVIDER_INACTIVITY: {
        label: 'A provider connection has stopped delivering',
        source: 'PROVIDER_STATE',
        severity: 'WARNING',
        remedy: 'Open Provider Connections for the tenant. The last attempt records the provider’s own error beside the last success, which separates "they are refusing us" from "we have stopped asking".',
        cooldownMinutes: 6 * 60,
    },
    /** §7: "queue backlog". */
    QUEUE_BACKLOG: {
        label: 'A queue is backing up',
        source: 'QUEUE',
        severity: 'WARNING',
        remedy: 'Check the worker is running and not crash-looping. A queue that grows while the worker is healthy usually means one job is failing and being retried.',
        cooldownMinutes: 60,
    },
    /** §7: "long-running jobs". */
    LONG_RUNNING_JOB: {
        label: 'A scheduled job has been running too long',
        source: 'SCHEDULER_STATE',
        severity: 'WARNING',
        remedy: 'The job holds a lease with a deadline; past it another replica may take over, so a job reported here has either stalled or is genuinely slow. Pipeline Health shows its recent runs and their durations.',
        cooldownMinutes: 3 * 60,
    },
};
export const PLATFORM_ALERT_RULE_KEYS = Object.keys(PLATFORM_ALERT_RULES);
/** The widened reading surface. See `ANOMALY_FEATURES` for why this exists. */
export function alertRule(key) {
    return PLATFORM_ALERT_RULES[key];
}
/**
 * §7's lifecycle: "OPEN, ACKNOWLEDGED, RESOLVED and REOPENED".
 *
 * ⚠️ **REOPENED IS NOT THE SAME AS OPEN, and that is the whole reason it is a
 * state.** An alert that fired, was resolved, and has come back says the fix did
 * not work — which is a different thing to do next from a condition nobody has
 * seen before. Collapsing it into OPEN would lose exactly that.
 */
export const ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'REOPENED'];
export const alertStatusSchema = z.enum(ALERT_STATUSES);
/**
 * ⚠️ **THE LIVE STATUSES.** Everything but RESOLVED counts as live: an
 * ACKNOWLEDGED alert is still a condition that has not cleared, and treating it
 * as closed is how a known problem falls off the list somebody is working from.
 */
export const LIVE_ALERT_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'REOPENED'];
export const platformAlertSchema = z.object({
    id: z.string(),
    rule: z.string(),
    label: z.string(),
    /** What this alert is ABOUT — a service, a connection, a queue. Groups repeats. */
    alertKey: z.string(),
    status: alertStatusSchema,
    severity: alertSeveritySchema,
    summary: z.string(),
    remedy: z.string(),
    source: z.string(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    /** How many evaluation runs have seen this condition. */
    occurrences: z.number(),
    /**
     * ⚠️ How many times it came BACK after being resolved. The fact that says a
     * fix did not hold, which a bare status cannot.
     */
    reopenCount: z.number(),
    lastResolvedAt: z.string().nullable(),
    acknowledgedAt: z.string().nullable(),
    acknowledgedBy: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    /**
     * ⚠️ NULL when the CONDITION cleared rather than a person closing it, and the
     * difference matters: one is evidence the problem went away, the other is
     * somebody saying they dealt with it.
     */
    resolvedBy: z.string().nullable(),
    notifiedAt: z.string().nullable(),
    details: z.record(z.unknown()),
});
/**
 * ⚠️ **PLAIN TEXT ONLY, and that is a decision rather than laziness.** The
 * summary carries a provider's own words and a queue name — arbitrary strings
 * from outside — so an HTML body would need escaping on the one message a
 * platform administrator must be able to trust. Text cannot be made to lie by
 * a payload.
 */
export function composePlatformAlertEmail(alert, env) {
    const rule = alertRule(alert.rule);
    /*
     * ⚠️ **THE ENVIRONMENT IS IN THE SUBJECT, because staging and production both
     * send mail.** §1 already records that `NODE_ENV` cannot separate them and
     * that a field which cannot is worse than absent, since a reader believes it.
     * The same applies in a mailbox: an alert whose environment is not on the
     * subject line is one somebody acts on in the wrong place.
     *
     * The SEVERITY is there too. A CRITICAL here means a line that mattered was
     * never written and can never be recovered, and somebody triaging a mailbox
     * needs that before they open anything.
     */
    const label = rule?.label ?? alert.rule;
    const subject = `[OIP ${env} · ${alert.severity}] ${label}`;
    const lines = [alert.summary, ''];
    /*
     * ⚠️ **A REOPENED ALERT SAYS SO, FIRST.** "This came back" is a materially
     * different fact from "this is new" — it says the previous fix did not hold —
     * and it is the one thing a reader who has seen this alert before needs at the
     * top rather than buried under figures.
     */
    if (alert.status === 'REOPENED') {
        lines.push(alert.reopenCount === 1
            ? 'This condition had been resolved and has come back.'
            : `This condition had been resolved and has come back — for the ${ordinal(alert.reopenCount)} time.`, '');
    }
    lines.push('What to do', rule?.remedy ??
        'This rule is not in the registry, so no remedy is recorded for it — which is itself worth reporting.', '', 'Detail', `  Rule          ${alert.rule}`, 
    // ⚠️ NOT labelled "Subject": this is read inside an email, where that word
    // already means the subject LINE. Found by reading a delivered message.
    `  Affects       ${alert.alertKey}`, `  First seen    ${alert.firstSeenAt.toISOString()}`, `  Seen          ${alert.occurrences} ${alert.occurrences === 1 ? 'time' : 'times'}`, `  Environment   ${env}`, '');
    /*
     * ⚠️ **IT SAYS WHAT WILL STOP IT, and only things that exist.** There is no
     * alerts screen and no acknowledge route yet, so an email inviting somebody to
     * acknowledge would be a control that lies — the defect this codebase records
     * more than any other. What is true today is the cooldown and the
     * auto-resolution, so that is what it says. A link belongs here the day the
     * screen does.
     */
    const repeat = rule
        ? `While the condition lasts this repeats at most once every ${describeAlertCooldown(rule.cooldownMinutes)}. It stops on its own when the condition clears.`
        : 'It stops on its own when the condition clears.';
    lines.push(repeat);
    return { subject, text: lines.join('\n') };
}
function ordinal(n) {
    const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
    return `${n}${suffix}`;
}
/**
 * ⚠️ **ONE wording, read by the EMAIL and by the SCREEN.** Both say how long
 * before an alert repeats, and "every 720 minutes" is arithmetic a reader should
 * not have to do — 12 hours is the same fact. A second copy is how the mailbox
 * and the page come to describe one cooldown differently.
 */
export function describeAlertCooldown(minutes) {
    if (minutes < 60)
        return `${minutes} minutes`;
    const hours = minutes / 60;
    if (hours === 1)
        return 'hour';
    if (Number.isInteger(hours))
        return `${hours} hours`;
    return `${minutes} minutes`;
}
// ── the API surface ─────────────────────────────────────────────────────────
/**
 * ⚠️ **`live` IS THE DEFAULT, and `all` is a different question.** The page is a
 * work queue: what has not cleared. History answers "did this happen before",
 * which is a second visit rather than the first thing anybody wants.
 */
export const platformAlertListQuerySchema = z
    .object({
    status: z.enum(['live', 'all']).default('live'),
    limit: z.coerce.number().int().min(1).max(200).default(100),
})
    .strict();
export const platformAlertListResponseSchema = z.object({
    alerts: z.array(platformAlertSchema),
    /** Everything not RESOLVED, whatever the list was filtered to. */
    liveCount: z.number(),
    /**
     * ⚠️ **WHEN THE EVALUATOR LAST SUCCEEDED, and an empty list is only
     * trustworthy with it.** Nothing here is computed on read: the rows are
     * whatever the last run left behind. If that job has stopped, this page does
     * not fail — it FREEZES, and an empty queue is exactly what a healthy platform
     * looks like. Without this the screen lies in the reassuring direction, which
     * is the `refreshedAt` argument the log dashboard already makes.
     *
     * NULL when the evaluator has never completed a run in the window, which is
     * STALE rather than fresh — a platform that has never evaluated has no verdict
     * to show, and that is not the same as having nothing to report.
     */
    lastEvaluatedAt: z.string().nullable(),
    stale: z.boolean(),
});
/**
 * ⚠️ **HOW LONG BEFORE THE LIST IS CALLED STALE — TWO evaluation intervals, not
 * one.** At exactly one, a perfectly healthy platform flickers between fresh and
 * stale on every page load, because the run takes a moment and the page may be
 * opened at any point in the cycle. A warning that appears on a working system
 * is one a reader learns to ignore.
 */
export const ALERT_EVALUATION_INTERVAL_MINUTES = 5;
export const ALERT_STALE_AFTER_MINUTES = ALERT_EVALUATION_INTERVAL_MINUTES * 2;
//# sourceMappingURL=platform-alerts.js.map